import {
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { Column } from "../Column";
import { stafferPets } from "../../lib/stafferPets";
import { cn } from "../../lib/utils";
import type {
  StafferPetPalette,
  StafferPetPaletteId,
  StafferPetSpecies,
  StafferPetTopperOffset,
} from "../../types";

const DEFAULT_SIZE_PX = 96;
const SOURCE_SIZE_PX = 256;
const RAINBOW_STOPS: Rgb[] = [
  [249, 81, 112],
  [255, 162, 76],
  [255, 225, 94],
  [75, 214, 149],
  [66, 175, 244],
  [151, 108, 239],
  [237, 112, 203],
];

const hexToRgb = (hex: string): [number, number, number] => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
];

type Rgb = [number, number, number];

// Parsed once per sprite; the pixel loop below runs ~65k times.
const getBandColors = (palette: StafferPetPalette): Rgb[] =>
  [
    palette.outline,
    palette.shadow,
    palette.body,
    palette.highlight,
    palette.belly,
    palette.eyeHighlight,
  ].map(hexToRgb);

const getPixelColor = (brightness: number, bandColors: Rgb[]): Rgb => {
  if (brightness < 52) return bandColors[0];
  if (brightness < 88) return bandColors[1];
  if (brightness < 142) return bandColors[2];
  if (brightness < 194) return bandColors[3];
  if (brightness < 232) return bandColors[4];
  return bandColors[5];
};

// Recolored pixels keyed by sprite + palette, shared by every instance: the
// shop grid and the yard draw many of the same pet. Capped (~256KB each).
const RECOLOR_CACHE_LIMIT = 64;
const recolorCache = new Map<string, ImageData>();

const cacheRecolor = (key: string, imageData: ImageData) => {
  if (recolorCache.size >= RECOLOR_CACHE_LIMIT) {
    const oldestKey = recolorCache.keys().next().value;
    if (oldestKey !== undefined) recolorCache.delete(oldestKey);
  }
  recolorCache.set(key, imageData);
};

const TOPPER_PLACEMENT_BY_SPECIES: Record<
  StafferPetSpecies,
  { headAnchorX: number; headAnchorY: number; widthScale: number }
> = {
  FOX: { headAnchorX: 136, headAnchorY: 72, widthScale: 1 },
  DRAGON: { headAnchorX: 149, headAnchorY: 75, widthScale: 0.9 },
  CAT: { headAnchorX: 146, headAnchorY: 72, widthScale: 1 },
  BLUE_JAY: { headAnchorX: 151, headAnchorY: 79, widthScale: 0.85 },
  CARDINAL: { headAnchorX: 154, headAnchorY: 79, widthScale: 0.85 },
  COCKATIEL: { headAnchorX: 168, headAnchorY: 72, widthScale: 0.85 },
  KINGFISHER: { headAnchorX: 135, headAnchorY: 62, widthScale: 0.9 },
  OWL: { headAnchorX: 132, headAnchorY: 56, widthScale: 0.95 },
  CROW: { headAnchorX: 155, headAnchorY: 64, widthScale: 0.85 },
  WREN: { headAnchorX: 143, headAnchorY: 72, widthScale: 0.8 },
  BALD_EAGLE: { headAnchorX: 133, headAnchorY: 29, widthScale: 0.8 },
  DUNE_WORM: { headAnchorX: 181, headAnchorY: 42, widthScale: 0.8 },
  BAT: { headAnchorX: 132, headAnchorY: 79, widthScale: 0.8 },
  PHOENIX: { headAnchorX: 163, headAnchorY: 60, widthScale: 0.8 },
};

export const StafferPetSprite = ({
  species,
  paletteId,
  sizePx = DEFAULT_SIZE_PX,
  className,
  isAnimated = false,
  isEvolved = false,
  isBouncing = false,
  isTripleBounce = false,
  hasPaletteGlow = true,
  topperId = null,
  topperOffset = { x: 0, y: 0 },
  onTopperOffsetChange,
  accessoryId = null,
  accessoryAdjustment = { x: 0, y: 0, scale: 1 },
  onAccessoryAdjustmentChange,
  accessoryId2 = null,
  accessoryAdjustment2 = { x: 0, y: 0, scale: 1 },
  onAccessoryAdjustmentChange2,
  onAccessoryDragStart,
}: {
  species: StafferPetSpecies;
  paletteId?: StafferPetPaletteId;
  sizePx?: number;
  className?: string;
  isAnimated?: boolean;
  isEvolved?: boolean;
  isBouncing?: boolean;
  isTripleBounce?: boolean;
  hasPaletteGlow?: boolean;
  topperId?: string | null;
  topperOffset?: StafferPetTopperOffset;
  onTopperOffsetChange?: (offset: StafferPetTopperOffset) => void;
  accessoryId?: string | null;
  accessoryAdjustment?: StafferPetTopperOffset;
  onAccessoryAdjustmentChange?: (adjustment: StafferPetTopperOffset) => void;
  accessoryId2?: string | null;
  accessoryAdjustment2?: StafferPetTopperOffset;
  onAccessoryAdjustmentChange2?: (adjustment: StafferPetTopperOffset) => void;
  onAccessoryDragStart?: (slot: 1 | 2) => void;
}) => {
  /* React Hooks */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fireGlowCanvasRef = useRef<HTMLCanvasElement>(null);
  const topperDragRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    offset: StafferPetTopperOffset;
  } | null>(null);
  const accessoryDragRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    adjustment: StafferPetTopperOffset;
    slot: 1 | 2;
  } | null>(null);
  const palette = useMemo(
    () => stafferPets.getPalette(species, paletteId),
    [paletteId, species],
  );
  const spriteSrc = useMemo(
    () => stafferPets.getSpriteSrc(species, isEvolved),
    [isEvolved, species],
  );
  // Dense lists opt out: blurred glows get expensive across many sprites.
  const hasGlow =
    hasPaletteGlow && paletteId
      ? stafferPets.GLOW_PALETTE_IDS.includes(paletteId)
      : false;
  const hasFireGlow = hasPaletteGlow && paletteId === "PHX_RED";
  const centerOffsetXPx = stafferPets.getSpriteCenterOffsetXPx(
    species,
    isEvolved,
  );
  const topper = stafferPets.getTopper(topperId);
  const accessory = stafferPets.getAccessory(accessoryId);
  const accessory2 =
    accessoryId2 === accessoryId
      ? null
      : stafferPets.getAccessory(accessoryId2);
  const topperPlacement = TOPPER_PLACEMENT_BY_SPECIES[species];
  const topperWidthPercent = topper
    ? topper.widthPercent *
      topperPlacement.widthScale *
      (topperOffset.scale || 1)
    : 0;
  const topperLeftPercent = topper
    ? (topperPlacement.headAnchorX / SOURCE_SIZE_PX) * 100 -
      (topper.anchorX / SOURCE_SIZE_PX) * topperWidthPercent +
      (topperOffset.x / SOURCE_SIZE_PX) * 100
    : 0;
  const topperTopPercent = topper
    ? (topperPlacement.headAnchorY / SOURCE_SIZE_PX) * 100 -
      (topper.anchorY / SOURCE_SIZE_PX) * topperWidthPercent +
      (topperOffset.y / SOURCE_SIZE_PX) * 100
    : 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    // CPU-backed: getImageData on a GPU canvas stalls the main thread, and a
    // full shop grid does dozens of them at once.
    const context = canvas?.getContext("2d", { willReadFrequently: true });
    if (!canvas || !context) return;

    const fireGlowContext = fireGlowCanvasRef.current?.getContext("2d");
    const clearSprite = () => {
      context.clearRect(0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);
      fireGlowContext?.clearRect(0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);
    };

    const bandColors = getBandColors(palette);
    const cacheKey = [
      "crystal-facets-v4",
      spriteSrc,
      species,
      isEvolved,
      paletteId,
      ...bandColors,
    ].join("|");
    const image = new Image();
    let hasRendered = false;
    const copyToFireGlow = () => {
      if (!fireGlowContext) return;
      fireGlowContext.clearRect(0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);
      fireGlowContext.drawImage(canvas, 0, 0);
    };
    const renderSprite = () => {
      // A cached image reports `complete` and still fires `load`; draw once.
      if (hasRendered) return;
      hasRendered = true;
      try {
        const cached = recolorCache.get(cacheKey);
        if (cached) {
          context.putImageData(cached, 0, 0);
          copyToFireGlow();
          return;
        }
        context.clearRect(0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);
        context.imageSmoothingEnabled = false;
        context.drawImage(image, 0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);

        const imageData = context.getImageData(
          0,
          0,
          SOURCE_SIZE_PX,
          SOURCE_SIZE_PX,
        );
        for (let index = 0; index < imageData.data.length; index += 4) {
          if (imageData.data[index + 3] === 0) continue;

          const sourceRed = imageData.data[index];
          const sourceGreen = imageData.data[index + 1];
          const sourceBlue = imageData.data[index + 2];
          const brightness =
            sourceRed * 0.299 + sourceGreen * 0.587 + sourceBlue * 0.114;
          if (species === "BAT" && isEvolved) {
            // The vampire cape and jewel are authored colors, not palette bands.
            if (
              sourceBlue > sourceRed * 1.2 &&
              sourceBlue > sourceGreen * 1.08
            ) {
              const capeShade = Math.min(
                42,
                Math.max(8, Math.round(brightness)),
              );
              imageData.data[index] = capeShade;
              imageData.data[index + 1] = capeShade;
              imageData.data[index + 2] = capeShade;
              continue;
            }
            if (
              sourceRed > 50 &&
              sourceRed > sourceGreen * 1.4 &&
              sourceRed > sourceBlue * 1.2
            ) {
              continue;
            }
          }
          let [red, green, blue] = getPixelColor(brightness, bandColors);
          if (
            paletteId === "RAINBOW" &&
            brightness >= 88 &&
            brightness < 232
          ) {
            const x = (index / 4) % SOURCE_SIZE_PX;
            const y = Math.floor(index / 4 / SOURCE_SIZE_PX);
            const position = Math.min(
              5.999,
              ((x * 0.35 + y * 0.65) / SOURCE_SIZE_PX) * 6,
            );
            const stop = Math.floor(position);
            const blend = position - stop;
            const shade = 0.55 + brightness / 360;
            [red, green, blue] = RAINBOW_STOPS[stop].map(
              (channel, channelIndex) =>
                Math.min(
                  255,
                  Math.round(
                    (channel * (1 - blend) +
                      RAINBOW_STOPS[stop + 1][channelIndex] * blend) *
                      shade,
                  ),
                ),
            ) as Rgb;
          }
          imageData.data[index] = red;
          imageData.data[index + 1] = green;
          imageData.data[index + 2] = blue;
        }

        context.putImageData(imageData, 0, 0);
        cacheRecolor(cacheKey, imageData);
        copyToFireGlow();
      } catch {
        clearSprite();
      }
    };

    const handleImageError = () => {
      clearSprite();
    };

    // Cache hit: skip loading the source image entirely.
    if (recolorCache.has(cacheKey)) {
      renderSprite();
      return;
    }
    image.addEventListener("load", renderSprite);
    image.addEventListener("error", handleImageError);
    image.src = spriteSrc;
    if (image.complete) renderSprite();

    return () => {
      image.removeEventListener("load", renderSprite);
      image.removeEventListener("error", handleImageError);
    };
  }, [hasFireGlow, isEvolved, palette, paletteId, species, spriteSrc]);

  /* Handlers */
  const handleTopperPointerDown = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    if (!onTopperOffsetChange) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    topperDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      offset: topperOffset,
    };
  };

  const handleTopperPointerMove = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    const drag = topperDragRef.current;
    if (
      !onTopperOffsetChange ||
      drag === null ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    const scaleToSource = SOURCE_SIZE_PX / sizePx;
    const x = Math.round(
      drag.offset.x + (event.clientX - drag.clientX) * scaleToSource,
    );
    const y = Math.round(
      drag.offset.y + (event.clientY - drag.clientY) * scaleToSource,
    );
    onTopperOffsetChange(
      stafferPets.clampTopperOffset(
        { x, y, scale: drag.offset.scale },
        topperId,
      ),
    );
  };

  const handleTopperPointerEnd = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    if (topperDragRef.current?.pointerId !== event.pointerId) return;
    topperDragRef.current = null;
  };

  const handleAccessoryPointerDown = (
    event: ReactPointerEvent<HTMLImageElement>,
    slot: 1 | 2,
  ) => {
    if (
      !(slot === 1 ? onAccessoryAdjustmentChange : onAccessoryAdjustmentChange2)
    )
      return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    onAccessoryDragStart?.(slot);
    accessoryDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      adjustment: slot === 1 ? accessoryAdjustment : accessoryAdjustment2,
      slot,
    };
  };

  const handleAccessoryPointerMove = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    const drag = accessoryDragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) {
      return;
    }
    const onChange =
      drag.slot === 1
        ? onAccessoryAdjustmentChange
        : onAccessoryAdjustmentChange2;
    const draggedAccessoryId = drag.slot === 1 ? accessoryId : accessoryId2;
    if (!onChange) return;

    const scaleToSource = SOURCE_SIZE_PX / sizePx;
    const x = Math.round(
      drag.adjustment.x + (event.clientX - drag.clientX) * scaleToSource,
    );
    const y = Math.round(
      drag.adjustment.y + (event.clientY - drag.clientY) * scaleToSource,
    );
    onChange(
      stafferPets.clampAccessoryAdjustment(
        {
          x,
          y,
          scale: drag.adjustment.scale,
          rotation: drag.adjustment.rotation,
        },
        draggedAccessoryId,
      ),
    );
  };

  const handleAccessoryPointerEnd = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    if (accessoryDragRef.current?.pointerId !== event.pointerId) return;
    accessoryDragRef.current = null;
  };

  /* Render */
  const renderAccessory = (slot: 1 | 2) => {
    const item = slot === 1 ? accessory : accessory2;
    const adjustment = slot === 1 ? accessoryAdjustment : accessoryAdjustment2;
    const onChange =
      slot === 1 ? onAccessoryAdjustmentChange : onAccessoryAdjustmentChange2;
    if (item === null) return null;
    const width = item.widthPercent * (adjustment.scale || 1);
    const left =
      50 -
      width / 2 +
      ((item.defaultX || 0) / SOURCE_SIZE_PX) * 100 +
      (adjustment.x / SOURCE_SIZE_PX) * 100;
    const top =
      50 -
      width / 2 +
      ((item.defaultY || 0) / SOURCE_SIZE_PX) * 100 +
      (adjustment.y / SOURCE_SIZE_PX) * 100;
    const isBehind = "layer" in item && item.layer === "BEHIND";

    return (
      <img
        alt=""
        src={item.src}
        draggable={false}
        onPointerCancel={handleAccessoryPointerEnd}
        onPointerDown={(event) => handleAccessoryPointerDown(event, slot)}
        onPointerMove={handleAccessoryPointerMove}
        onPointerUp={handleAccessoryPointerEnd}
        className={cn(
          "absolute h-auto max-w-none select-none",
          isBehind ? "z-0" : "z-20",
          onChange
            ? "pointer-events-auto cursor-grab touch-none active:cursor-grabbing"
            : "pointer-events-none",
        )}
        style={{
          imageRendering: "pixelated",
          left: `${left.toString()}%`,
          top: `${top.toString()}%`,
          transform: `rotate(${(item.rotation + (adjustment.rotation ?? 0)).toString()}deg)`,
          width: `${width.toString()}%`,
        }}
      />
    );
  };

  const render = () => {
    return (
      <Column
        className={cn(
          "relative h-full w-full",
          isBouncing &&
            (isTripleBounce
              ? "staffer-pet-bounce-triple"
              : "staffer-pet-bounce"),
          !isBouncing && isAnimated && "staffer-pet-float",
          className,
        )}
        style={{
          height: sizePx,
          width: sizePx,
          // `translate` (not `transform`) so it stacks with the bounce animations.
          translate:
            centerOffsetXPx === 0
              ? undefined
              : `${((centerOffsetXPx / SOURCE_SIZE_PX) * 100).toString()}% 0`,
        }}
      >
        <canvas
          ref={canvasRef}
          height={SOURCE_SIZE_PX}
          width={SOURCE_SIZE_PX}
          className={cn(
            "pointer-events-none relative z-10 block h-full w-full select-none",
            hasFireGlow && "staffer-pet-fire-palette-glow",
            paletteId === "RAINBOW" &&
              hasPaletteGlow &&
              "staffer-pet-rainbow-shift",
          )}
          style={{
            imageRendering: "pixelated",
            filter:
              hasGlow && !hasFireGlow
                ? `drop-shadow(0 0 1px ${palette.body}) drop-shadow(0 0 3px ${palette.body}) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4))`
                : undefined,
          }}
        />
        {hasFireGlow ? (
          <canvas
            ref={fireGlowCanvasRef}
            height={SOURCE_SIZE_PX}
            width={SOURCE_SIZE_PX}
            className="staffer-pet-fire-palette-pulse pointer-events-none absolute inset-0 z-10 block h-full w-full select-none"
            style={{ imageRendering: "pixelated" }}
          />
        ) : null}
        {renderAccessory(1)}
        {renderAccessory(2)}
        {topper ? (
          <img
            alt=""
            src={topper.src}
            draggable={false}
            onPointerCancel={handleTopperPointerEnd}
            onPointerDown={handleTopperPointerDown}
            onPointerMove={handleTopperPointerMove}
            onPointerUp={handleTopperPointerEnd}
            className={cn(
              "absolute z-30 h-auto max-w-none select-none",
              onTopperOffsetChange
                ? "pointer-events-auto cursor-grab touch-none active:cursor-grabbing"
                : "pointer-events-none",
            )}
            style={{
              imageRendering: "pixelated",
              left: `${topperLeftPercent.toString()}%`,
              top: `${topperTopPercent.toString()}%`,
              width: `${topperWidthPercent.toString()}%`,
            }}
          />
        ) : null}
      </Column>
    );
  };

  return render();
};
